import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface FeeInstallmentDto {
  installmentNumber: number;
  amount: number;
  dueDate: string; // ISO date string
}

export interface FeeStructureDto {
  id?: number;
  sessionId: number;
  classId: number;
  feeType: string; // "TUTION_FEES"
  totalAnnualFees: number;
  totalInstallments: number;
  feeInstallments: FeeInstallmentDto[];
  className?: string;
  sessionName?: string;
}

export interface BankDetailsDto {
  id: number; // school id expected by backend
  bankName: string;
  accountNo: string;
  ifscCode: string;
}

const API_URL = `${process.env.REACT_APP_API_URL}/feeStructure`;

// Fetch all fee structures (old endpoint with schoolId)
const getAllFeeStructures = async (schoolId?: number): Promise<FeeStructureDto[]> => {
  try {
    const response = await axios.get(API_URL, {
      params: schoolId ? { schoolId } : undefined,
    });
    return response.data;
  } catch (error: any) {
    // If endpoint doesn't exist (404) or other non-auth errors, return empty array
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return [];
    }
    // Re-throw auth errors (401) to let axios interceptor handle them
    throw error;
  }
};

// Fetch all fee structures (GET /feeStructure/get endpoint)
const getAllFeeStructuresNew = async (): Promise<FeeStructureDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/get`);
    // Ensure we return an array even if response.data is not an array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // If response.data is not an array, return empty array
    return [];
  } catch (error: any) {
    // Log the error for debugging

    // If endpoint doesn't exist (404) or server errors (500), return empty array
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return [];
    }
    // Handle 401 errors - let axios interceptor handle logout, but return empty array to prevent UI crash
    if (error?.response?.status === 401) {
      // Still return empty array to prevent UI crash, but the interceptor will handle logout
      return [];
    }
    // Re-throw other errors so React Query can handle them
    throw error;
  }
};

// Fetch fee structure by sessionId and classId
const getFeeStructureBySessionAndClass = async (sessionId: number, className: String): Promise<FeeStructureDto | null> => {
  try {
    const response = await axios.get(`${API_URL}/getBySessionAndClass`, {
      params: { sessionId, className }
    });
    return response.data;
  } catch (error: any) {
    // If endpoint doesn't exist (404) or no data found, return null
    if (error?.response?.status === 404) {
      return null;
    }
    // Re-throw auth errors (401) to let axios interceptor handle them
    if (error?.response?.status === 401) {
      throw error;
    }
    // For other errors, log and return null
    return null;
  }
};

// Create a new fee structure
const createFeeStructure = async (data: FeeStructureDto): Promise<FeeStructureDto> => {
  const response = await axios.post(`${API_URL}/add`, data);
  return response.data;
};

// Update a fee structure
const updateFeeStructure = async (data: FeeStructureDto): Promise<FeeStructureDto> => {
  const response = await axios.put(API_URL, data);
  return response.data;
};

// Delete a fee structure
const deleteFeeStructure = async (sessionId: number, classId: number, feeType: string): Promise<void> => {
  await axios.delete(API_URL, {
    params: { sessionId, classId, feeType }
  });
};

// Add bank details
const addBankDetails = async (data: BankDetailsDto): Promise<any> => {
  const response = await axios.post(`${API_URL}/addBankDetails`, data);
  return response.data;
};

// Fetch bank details for a school
const getBankDetails = async (schoolId: number): Promise<BankDetailsDto | null> => {
  try {
    // Fetch school data which should contain bank details
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/schools/${schoolId}`);
    const schoolData = response.data;

    // Extract bank details from school data
    if (schoolData.bankName || schoolData.accountNo || schoolData.ifscCode) {
      return {
        id: schoolId,
        bankName: schoolData.bankName || "",
        accountNo: schoolData.accountNo || "",
        ifscCode: schoolData.ifscCode || "",
      };
    }
    return null;
  } catch (error: any) {
    // If school not found or no bank details, return null
    if (error?.response?.status === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
};

export function useFeeStructure(schoolId?: number, tenantLoading: boolean = false, enableQuery: boolean = false) {
  const queryClient = useQueryClient();

  // Old query with schoolId - DISABLED (not used anymore, use /get endpoint instead)
  const feeStructuresQuery = useQuery<FeeStructureDto[], Error>({
    queryKey: ["feeStructures", schoolId],
    queryFn: () => getAllFeeStructures(schoolId),
    enabled: false, // Disabled - we use the /get endpoint instead
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // New query for /get endpoint (no schoolId required) - THIS IS THE ONE WE USE
  const feeStructuresGetQuery = useQuery<FeeStructureDto[], Error>({
    queryKey: ["feeStructures", "get"],
    queryFn: () => getAllFeeStructuresNew(),
    enabled: enableQuery && !tenantLoading, // Only fetch when explicitly enabled
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const createFeeStructureMutation = useMutation({
    mutationFn: createFeeStructure,
    onSuccess: () => {
      // Invalidate both query keys to refresh the data
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const updateFeeStructureMutation = useMutation({
    mutationFn: updateFeeStructure,
    onSuccess: () => {
      // Invalidate both query keys to refresh the data
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const deleteFeeStructureMutation = useMutation({
    mutationFn: ({ sessionId, classId, feeType }: { sessionId: number; classId: number; feeType: string }) =>
      deleteFeeStructure(sessionId, classId, feeType),
    onSuccess: () => {
      // Invalidate both query keys to refresh the data
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const addBankDetailsMutation = useMutation({
    mutationFn: addBankDetails,
    onSuccess: () => {
      // Invalidate school queries to refresh bank details
      queryClient.invalidateQueries({ queryKey: ["schoolById"] });
      queryClient.invalidateQueries({ queryKey: ["schools"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
    },
  });

  // Query to fetch bank details for a school
  const bankDetailsQuery = useQuery<BankDetailsDto | null, Error>({
    queryKey: ["bankDetails", schoolId],
    queryFn: () => schoolId ? getBankDetails(schoolId) : Promise.resolve(null),
    enabled: !!schoolId,
    staleTime: 0, // Always fetch fresh data when query is enabled
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    feeStructuresQuery,
    feeStructuresGetQuery,
    createFeeStructureMutation,
    updateFeeStructureMutation,
    deleteFeeStructureMutation,
    addBankDetailsMutation,
    bankDetailsQuery,
    getFeeStructureBySessionAndClass,
  };
}

// Hook to fetch fee structure by sessionId and classId
export function useFeeStructureBySessionAndClass(sessionId?: number, className?: String, enabled: boolean = true) {
  return useQuery<FeeStructureDto | null, Error>({
    queryKey: ["feeStructure", "bySessionAndClass", sessionId, className],
    queryFn: () => {
      if (!sessionId || !className) {
        return Promise.resolve(null);
      }
      return getFeeStructureBySessionAndClass(sessionId, className);
    },
    enabled: enabled && !!sessionId && !!className,
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

